// src/pages/TalleresCrear/components/Paso1Fechas.jsx
//
// Este paso pide TRES fechas. Llegó a ocupar 469 líneas con diez <Alert>, la
// mayoría explicando de antemano cosas que solo importan al rellenar un campo
// concreto, o repitiendo en prosa lo que el usuario acababa de escribir.
//
// Criterio aplicado:
//  · Explicación estática  → `tooltip` del label (se pide cuando hace falta).
//  · Ayuda breve y siempre útil → `extra` del campo.
//  · Eco de lo ya escrito  → un resumen compacto, no un bloque de texto.
//  · Aviso con consecuencia real → se queda como <Alert>, que para eso está.
import React from 'react';
import { Form, DatePicker, Card, Alert, Tag, Space } from 'antd';
import { ClockCircleOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

const { RangePicker } = DatePicker;

/** "1 día" / "2 días", en vez de arrastrar un "día(s)" o un "1 días". */
const enDias = (n) => `${n} ${n === 1 ? 'día' : 'días'}`;

/** Fila de un resumen: etiqueta discreta y valor destacado. */
const Dato = ({ etiqueta, children }) => (
  <div className="flex flex-wrap items-baseline gap-x-2">
    <span className="text-xs text-slate-500">{etiqueta}</span>
    <span className="text-sm font-medium text-slate-800">{children}</span>
  </div>
);

/** Panel de resumen: sustituye a los <Alert> que solo repetían lo ya escrito. */
const Resumen = ({ children }) => (
  <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 space-y-2">
    {children}
  </div>
);

export default function Paso1Fechas({ form }) {
  // Watch de campos
  const watchInscripcionInicio = Form.useWatch('inscripcionInicio', form);
  const watchInscripcionFin = Form.useWatch('inscripcionFin', form);
  const watchFechasActividades = Form.useWatch('fechasActividades', form);
  const watchTipoDuracion = Form.useWatch('tipoDuracionSuscripcion', form);

  // Validar fechas de inscripción (ahora con hora)
  const tieneFechasInscripcion = watchInscripcionInicio && watchInscripcionFin;

  // Validar fechas de actividades
  const tieneFechasActividades = watchFechasActividades &&
    Array.isArray(watchFechasActividades) &&
    watchFechasActividades.length === 2 &&
    watchFechasActividades[0] &&
    watchFechasActividades[1];

  // Calcular duración del taller en días
  const diasTaller = tieneFechasActividades
    ? dayjs(watchFechasActividades[1]).diff(dayjs(watchFechasActividades[0]), 'day')
    : null;

  // Detectar si es taller corto
  const esTallerCorto = diasTaller !== null && diasTaller < 30;

  // Validar coherencia entre fechas de inscripción y actividades
  const fechasIncoherentes = tieneFechasInscripcion && tieneFechasActividades &&
    dayjs(watchInscripcionFin).isAfter(dayjs(watchFechasActividades[0]));

  // Verificar si ya pasó la hora de apertura
  const yaAbrio = watchInscripcionInicio &&
    dayjs(watchInscripcionInicio).isBefore(dayjs());

  // Verificar si la apertura es hoy
  const aperturaEsHoy = watchInscripcionInicio &&
    dayjs(watchInscripcionInicio).isSame(dayjs(), 'day');

  // Calcular tiempo hasta apertura
  const tiempoHastaApertura = watchInscripcionInicio && !yaAbrio
    ? {
        dias: dayjs(watchInscripcionInicio).diff(dayjs(), 'day'),
        horas: dayjs(watchInscripcionInicio).diff(dayjs(), 'hour') % 24,
        minutos: dayjs(watchInscripcionInicio).diff(dayjs(), 'minute') % 60
      }
    : null;

  // Obtener descripción de la modalidad
  const getDescripcionModalidad = () => {
    switch (watchTipoDuracion) {
      case 'mes_calendario':
        return 'mismo día cada mes (15/01 → 15/02)';
      case 'fin_de_mes':
        return 'hasta fin de mes (15/01 → 31/01)';
      case 'mes_corrido':
        return 'siempre 30 días exactos';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-4">

      {/* ── Periodo de actividades ── */}
      <Card title="Periodo de actividades" size="small">
        <Form.Item
          label="Primer y último día de clases"
          name="fechasActividades"
          tooltip={
            <div className="space-y-1">
              <p>Cuándo empieza y termina realmente el taller.</p>
              <p>
                Si lo defines, las suscripciones arrancan el día de inicio aunque
                la persona pague antes. Los talleres de menos de 30 días se
                detectan solos y la suscripción dura exactamente ese periodo.
              </p>
            </div>
          }
          extra={
            !tieneFechasActividades
              ? 'Si lo dejas vacío, cada suscripción empieza el día de la compra.'
              : undefined
          }
          rules={[
            {
              validator: async (_, value) => {
                if (value && value[0] && value[1]) {
                  const inicio = dayjs(value[0]);
                  const fin = dayjs(value[1]);

                  if (fin.isBefore(inicio) || fin.isSame(inicio, 'day')) {
                    throw new Error('La fecha de fin debe ser posterior a la fecha de inicio');
                  }
                }
              }
            }
          ]}
        >
          <RangePicker
            size="large"
            format="DD/MM/YYYY"
            placeholder={['Primer día de clases', 'Último día de clases']}
            className="w-full"
            disabledDate={(current) => {
              if (!current) return false;
              const hoy = dayjs().endOf('day');
              return current.isBefore(hoy) || current.isSame(hoy, 'day');
            }}
          />
        </Form.Item>

        {tieneFechasActividades && (
          <>
            <Resumen>
              <Dato etiqueta="Del">
                {dayjs(watchFechasActividades[0]).format('DD/MM/YYYY')}
                <span className="mx-2 text-slate-400">→</span>
                {dayjs(watchFechasActividades[1]).format('DD/MM/YYYY')}
              </Dato>
              <Space size={[4, 4]} wrap>
                <Tag color={esTallerCorto ? 'orange' : 'blue'}>{enDias(diasTaller)}</Tag>
                {/* El tipo de duración se elige en el paso anterior; si aún no
                    hay valor, el tag saldría como "Suscripción:" a secas. */}
                {!esTallerCorto && getDescripcionModalidad() && (
                  <Tag>Suscripción: {getDescripcionModalidad()}</Tag>
                )}
              </Space>
              <Dato etiqueta="Las suscripciones">
                {esTallerCorto
                  ? `durarán exactamente ${enDias(diasTaller)}`
                  : `empezarán el ${dayjs(watchFechasActividades[0]).format('DD/MM/YYYY')}, aunque se compren antes`}
              </Dato>
            </Resumen>

            {/* Se queda como aviso: cambia la duración de lo que la gente compra. */}
            {esTallerCorto && (
              <Alert
                className="mt-3"
                type="warning"
                showIcon
                message={`Taller corto: ${diasTaller} días`}
                description="Al durar menos de 30 días, la suscripción cubre exactamente el periodo de actividades, sin importar el plan que se contrate."
              />
            )}
          </>
        )}
      </Card>

      {/* ── Periodo de inscripción ── */}
      <Card title="Periodo de inscripción" size="small">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Form.Item
            label="Apertura de inscripciones"
            name="inscripcionInicio"
            tooltip="Fecha y hora exactas en que se abren. Antes de ese momento nadie puede inscribirse."
            rules={[
              {
                validator: async (_, value) => {
                  if (value) {
                    // Validar que no sea en el pasado
                    if (dayjs(value).isBefore(dayjs())) {
                      throw new Error('La fecha/hora de apertura no puede ser en el pasado');
                    }

                    // Validar coherencia con fin de inscripción
                    if (watchInscripcionFin && dayjs(value).isAfter(dayjs(watchInscripcionFin))) {
                      throw new Error('La apertura debe ser antes del cierre');
                    }

                    // Validar coherencia con inicio de actividades
                    if (tieneFechasActividades && dayjs(value).isAfter(dayjs(watchFechasActividades[0]))) {
                      throw new Error('La apertura debe ser antes del inicio de actividades');
                    }
                  }
                }
              }
            ]}
          >
            <DatePicker
              showTime={{
                format: 'HH:mm',
                minuteStep: 15,
                showNow: false,
              }}
              format="DD/MM/YYYY HH:mm"
              placeholder="Selecciona fecha y hora"
              className="w-full"
              size="large"
              disabledDate={(current) => current && current < dayjs().startOf('day')}
            />
          </Form.Item>

          <Form.Item
            label="Cierre de inscripciones"
            name="inscripcionFin"
            tooltip="Fecha y hora exactas en que se cierran. Después de ese momento se cierran solas, sin que haya que hacer nada."
            rules={[
              {
                validator: async (_, value) => {
                  if (value) {
                    // Validar que sea después del inicio
                    if (watchInscripcionInicio && dayjs(value).isBefore(dayjs(watchInscripcionInicio))) {
                      throw new Error('El cierre debe ser después de la apertura');
                    }

                    // Validar coherencia con inicio de actividades
                    if (tieneFechasActividades && dayjs(value).isAfter(dayjs(watchFechasActividades[0]))) {
                      throw new Error('El cierre debe ser antes o el mismo día del inicio de actividades');
                    }
                  }
                }
              }
            ]}
          >
            <DatePicker
              showTime={{
                format: 'HH:mm',
                minuteStep: 15,
                showNow: false,
                defaultValue: dayjs().hour(23).minute(59),
              }}
              format="DD/MM/YYYY HH:mm"
              placeholder="Selecciona fecha y hora"
              className="w-full"
              size="large"
              disabledDate={(current) => {
                if (!current) return false;
                if (watchInscripcionInicio) {
                  return current < dayjs(watchInscripcionInicio).startOf('day');
                }
                return current < dayjs().startOf('day');
              }}
            />
          </Form.Item>
        </div>

        {!tieneFechasInscripcion && (
          <p className="text-xs text-slate-500 -mt-2">
            Si lo dejas vacío, las inscripciones se abren y pausan a mano desde el estado del taller.
          </p>
        )}

        {/* Se queda como error: bloquea el avance y hay que corregirlo. */}
        {fechasIncoherentes && (
          <Alert
            className="mt-3"
            type="error"
            showIcon
            icon={<WarningOutlined />}
            message="Fechas incoherentes"
            description="Las inscripciones deben cerrar antes o el mismo día en que comienzan las actividades."
          />
        )}

        {tieneFechasInscripcion && !fechasIncoherentes && (
          <Resumen>
            <Dato etiqueta="Abre">
              {dayjs(watchInscripcionInicio).format('dddd D [de] MMMM, HH:mm')} h
            </Dato>
            <Dato etiqueta="Cierra">
              {dayjs(watchInscripcionFin).format('dddd D [de] MMMM, HH:mm')} h
            </Dato>
            <Space size={[4, 4]} wrap>
              <Tag color={yaAbrio ? 'red' : 'blue'}>
                Ventana de {enDias(dayjs(watchInscripcionFin).diff(dayjs(watchInscripcionInicio), 'day'))}
              </Tag>
              {!yaAbrio && tiempoHastaApertura && (
                <Tag color={aperturaEsHoy ? 'orange' : 'cyan'} icon={<ClockCircleOutlined />}>
                  {aperturaEsHoy
                    ? `Abre hoy en ${tiempoHastaApertura.horas}h ${tiempoHastaApertura.minutos}min`
                    : `Abre en ${enDias(tiempoHastaApertura.dias)}`}
                </Tag>
              )}
              {tieneFechasActividades && (
                <Tag>
                  {enDias(dayjs(watchFechasActividades[0]).diff(dayjs(watchInscripcionFin), 'day'))} de
                  espera hasta el inicio
                </Tag>
              )}
            </Space>
            <p className="text-xs text-slate-500 m-0">Horario de Chile continental (UTC−3).</p>
          </Resumen>
        )}

        {/* Se queda como aviso: la configuración no hará lo que parece. */}
        {yaAbrio && (
          <Alert
            className="mt-3"
            type="warning"
            showIcon
            message="Esa hora de apertura ya pasó"
            description="Con esta configuración las inscripciones quedarán abiertas de inmediato al crear el taller."
          />
        )}
      </Card>
    </div>
  );
}
